// Phase 8.PH — Responsive KPI value.
//
// UX contract (post-production feedback tune-up):
//   * Prefer the EXACT locale-formatted string at the LARGEST visual
//     size (text-2xl sm:text-3xl). Normal 4-5-digit values such as
//     $19,986.80 must NOT be shrunk.
//   * Only when the exact string would actually overflow the card
//     container do we step down through the tier scale
//     (xl → lg → md → sm) one step at a time — measured via real
//     `scrollWidth > clientWidth` overflow, not via char-count heuristics.
//   * If NONE of the tiers fit, drop to locale-aware compact notation
//     (e.g. "$1.24M") as the last resort.
//   * Always preserve the exact locale-formatted value in `title` and
//     `aria-label` so screen readers and hover retain the full value.
//   * ResizeObserver drives re-measurement when the card resizes; a
//     deterministic length-based fallback keeps the component sane
//     when RO is unavailable (SSR / older browsers / jsdom).
//   * Values are visually centered inside their card.
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { fmtCurrency, fmtCurrencyCompact } from "@/components/portfolio/_format";

// Visual tiers, biggest → smallest. Each tier drops the visible size
// modestly so a stepped-down value still reads as a primary KPI.
const SIZE_TIERS = [
  { key: "xl", cls: "text-2xl sm:text-3xl" },
  { key: "lg", cls: "text-xl sm:text-2xl" },
  { key: "md", cls: "text-lg sm:text-xl" },
  { key: "sm", cls: "text-base sm:text-lg" },
];

// Runtime check — evaluated in the effect so tests can monkey-patch
// `window.ResizeObserver` after the module has been imported.
function _hasResizeObserverNow() {
  return typeof window !== "undefined"
    && typeof window.ResizeObserver === "function";
}

// Deterministic fallback tier chooser for environments without a real
// container measurement (SSR / initial paint / no ResizeObserver).
// Deliberately generous at the top tier so normal 4-5 digit currency
// values always start at xl and never appear pre-shrunk.
function _fallbackTier(formatted) {
  const s = String(formatted || "");
  if (s.length <= 12) return { tierIdx: 0, compact: false };  // $19,986.80
  if (s.length <= 14) return { tierIdx: 1, compact: false };  // $1,234,567.89
  if (s.length <= 16) return { tierIdx: 2, compact: false };  // $12,345,678.90
  if (s.length <= 20) return { tierIdx: 3, compact: false };  // huge but readable
  return { tierIdx: 3, compact: true };                        // fall back to compact
}

/**
 * ResponsiveKpiValue
 * ------------------
 * Renders a currency value that visually fits its parent card, using
 * a real overflow measurement pass. The tier is stepped down only if
 * `scrollWidth > clientWidth`; compact is used only when even the
 * smallest tier would still overflow.
 */
export default function ResponsiveKpiValue({
  value,
  currency = "USD",
  signed = false,
  ifNull = "—",
  className = "",
  colorClass = "",
  testId,
  ariaLabelBase,
}) {
  const containerRef = useRef(null);
  const spanRef = useRef(null);
  const [tierIdx, setTierIdx] = useState(0);
  const [useCompact, setUseCompact] = useState(false);
  // Bump this to force a re-measurement pass (used by ResizeObserver
  // and value changes).
  const [nonce, setNonce] = useState(0);

  const exact = useMemo(
    () => fmtCurrency(value, { currency, signed, ifNull }),
    [value, currency, signed, ifNull],
  );
  const compact = useMemo(
    () => fmtCurrencyCompact(value, { currency, signed, ifNull }),
    [value, currency, signed, ifNull],
  );

  // Reset to the largest tier on every relevant input change so the
  // measurement pass can find the true best fit rather than sticking
  // at a previously-picked smaller tier.
  useLayoutEffect(() => {
    setTierIdx(0);
    setUseCompact(false);
  }, [exact, compact]);

  // The core overflow-measurement pass. Runs on every render after
  // layout — if the span still overflows its container we step down
  // one tier. If we exhaust the tiers, we switch to compact. Because
  // `setTierIdx` triggers a re-render, this recurses one tier at a
  // time until the value fits (or compact is engaged).
  useLayoutEffect(() => {
    const container = containerRef.current;
    const span = spanRef.current;
    if (!container || !span) return;
    if (value === null || value === undefined || Number.isNaN(value)) return;
    // If already showing compact at smallest tier, nothing to do.
    if (useCompact) return;
    // Give the browser one frame worth of layout. `scrollWidth` is a
    // live measurement post-layout so we can read it inline.
    const spanW = span.scrollWidth;
    const contW = container.clientWidth;
    // A generous 4px slack so sub-pixel rounding does not cause an
    // oscillation between two adjacent tiers.
    if (spanW > contW + 4) {
      if (tierIdx < SIZE_TIERS.length - 1) {
        setTierIdx(tierIdx + 1);
      } else {
        setUseCompact(true);
      }
    }
  }, [tierIdx, useCompact, exact, compact, nonce, value]);

  // ── Observe container resize so responsive layouts re-measure. ─────
  useEffect(() => {
    if (!_hasResizeObserverNow() || !containerRef.current) return undefined;
    const el = containerRef.current;
    let lastW = el.clientWidth || 0;
    const ro = new window.ResizeObserver((entries) => {
      for (const entry of entries) {
        const w = Math.floor(entry.contentRect?.width || 0);
        if (Math.abs(lastW - w) > 1) {
          lastW = w;
          // Reset to xl and re-run the measurement chain so the layout
          // can UP-scale again when the card grows wider.
          setTierIdx(0);
          setUseCompact(false);
          setNonce((n) => n + 1);
        }
      }
    });
    ro.observe(el);
    return () => { try { ro.disconnect(); } catch { /* ignore */ } };
  }, []);

  // Window resize fallback (belt-and-suspenders for browsers where
  // ResizeObserver misses layout shifts).
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const onResize = () => {
      setTierIdx(0);
      setUseCompact(false);
      setNonce((n) => n + 1);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Length-based fallback for environments without ResizeObserver or
  // before the first measurement pass completes. Only kicks in when
  // no DOM measurement is possible (SSR / jsdom without RO).
  const preferFallback = useMemo(() => {
    if (typeof window === "undefined") return true;
    return !_hasResizeObserverNow();
  }, []);
  const fallback = useMemo(() => _fallbackTier(exact), [exact]);
  const effectiveTierIdx = preferFallback ? fallback.tierIdx : tierIdx;
  const effectiveCompact = preferFallback ? fallback.compact : useCompact;

  const tier = SIZE_TIERS[effectiveTierIdx] || SIZE_TIERS[0];
  const text = value === null || value === undefined || Number.isNaN(value)
    ? exact
    : (effectiveCompact ? compact : exact);
  const ariaLabel = ariaLabelBase ? `${ariaLabelBase}: ${exact}` : exact;

  return (
    <div
      ref={containerRef}
      className="min-w-0 w-full flex items-center justify-start"
      data-kpi-container="true"
      data-kpi-tier={tier.key}
      data-kpi-compact={effectiveCompact ? "true" : "false"}
    >
      <span
        ref={spanRef}
        data-testid={testId}
        title={String(exact ?? "")}
        aria-label={ariaLabel}
        className={`font-display ${tier.cls} tabular leading-none whitespace-nowrap ${colorClass} ${className}`}
        style={{ fontVariantNumeric: "tabular-nums" }}
      >
        {text}
      </span>
    </div>
  );
}
